import { useState, useCallback } from 'react';
import { apiFetch } from '../utils/apiClient';

// 고객 목록(고객관리 탭, 대시보드 최근활동)에서 공통으로 쓰는
// 적립/쿠폰조회/쿠폰사용 API 호출을 모아둔 훅
const useCustomerActions = (storeInfo) => {
  const [stampRules, setStampRules] = useState([]);
  const [actioningPhone, setActioningPhone] = useState(null);

  const fetchStampRules = useCallback(async () => {
    if (!storeInfo?.id) return;
    try {
      const response = await apiFetch(
        `/api/settings/stamp-rules?store_id=${encodeURIComponent(storeInfo.id)}`
      );
      const result = await response.json();
      if (result.success) {
        setStampRules(result.data || []);
      }
    } catch (err) {
      // 규칙 조회 실패는 조용히 무시(적립 자체는 계속 동작해야 함)
    }
  }, [storeInfo]);

  const callCustomerApi = useCallback(
    async (phone, path, { method = 'POST', body } = {}) => {
      const response = await apiFetch(
        `/api/customers/${encodeURIComponent(phone)}${path}?store_id=${encodeURIComponent(storeInfo.id)}`,
        {
          method,
          body: body ? JSON.stringify(body) : undefined,
        }
      );
      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || result.message || '요청이 실패했습니다.');
      }
      return result;
    },
    [storeInfo]
  );

  // 태블릿의 일반 적립(/stamp)은 손님용 키오스크라 60초 재시도 방지 +
  // 재적립 제한 시간이 걸리지만, admin 웹은 사장님 본인이 직접 조작하는
  // 도구라 그 제한이 오히려 불편하다("방금 찍었는데 또 안 찍힘"). 태블릿의
  // "관리자 모드 즉시 적립"과 같은 /stamp/force를 그대로 쓴다.
  const addStamp = useCallback(
    async (phone) => {
      setActioningPhone(phone);
      try {
        return await callCustomerApi(phone, '/stamp/force');
      } finally {
        setActioningPhone(null);
      }
    },
    [callCustomerApi]
  );

  // 고객 한 명의 최신 정보만 조회한다. 목록 전체를 다시 불러오면 정렬 순서상
  // 방금 처리한 고객이 맨 위로 튀어버리므로, 액션 후에는 이걸로 그 자리에서만 갱신한다.
  const fetchCustomer = useCallback(
    async (phone) => {
      const response = await apiFetch(
        `/api/customers/${encodeURIComponent(phone)}?store_id=${encodeURIComponent(storeInfo.id)}`
      );
      const result = await response.json();
      if (!result.success) {
        throw new Error(result.message || '고객 정보를 불러오지 못했습니다.');
      }
      return result.data;
    },
    [storeInfo]
  );

  const fetchCoupons = useCallback(
    async (phone) => {
      const response = await apiFetch(
        `/api/customers/${encodeURIComponent(phone)}/coupons?store_id=${encodeURIComponent(storeInfo.id)}`
      );
      const result = await response.json();
      if (!result.success) {
        throw new Error(result.message || '쿠폰 조회에 실패했습니다.');
      }
      return result.data || [];
    },
    [storeInfo]
  );

  const redeemCoupon = useCallback(
    async (phone, couponNumber) => {
      setActioningPhone(phone);
      try {
        return await callCustomerApi(phone, '/coupon/use', { body: { number: couponNumber } });
      } finally {
        setActioningPhone(null);
      }
    },
    [callCustomerApi]
  );

  return {
    stampRules,
    fetchStampRules,
    actioningPhone,
    addStamp,
    fetchCoupons,
    fetchCustomer,
    redeemCoupon,
  };
};

export default useCustomerActions;
